import {
  Avatar,
  Box,
  Divider,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography
} from "@mui/material";
import ChevronLeft from "@mui/icons-material/ChevronLeft";
import ChevronRight from "@mui/icons-material/ChevronRight";
import GridViewOutlined from "@mui/icons-material/GridViewOutlined";
import TuneOutlined from "@mui/icons-material/TuneOutlined";
import React from "react";
import { NAV_ICONS, NAV_ICON_FALLBACK } from "../../../models/navIcons";
import { tokens } from "../../../theme";
import LogoutButton from "./logoutButton";
import { LOGO_HEIGHT, onChrome, type ShellChromeProps } from "./shellChrome";

/** Icons-only width. Wide enough for a 24px icon plus the 8px item margins. */
export const RAIL_COLLAPSED_WIDTH = 72;

const ITEM_HEIGHT = 48;

// Roboto 12px at the theme's eyebrow tracking, per the preview — the rail
// group label is not a mono eyebrow in the MUI directions.
const groupLabelSx = {
  px: "16px",
  pt: "16px",
  pb: "8px",
  fontSize: tokens.typography.caption,
  fontWeight: 500,
  letterSpacing: tokens.typography.eyebrowTracking,
  color: onChrome.muted,
  whiteSpace: "nowrap" as const,
  overflow: "hidden"
};

const itemSx = (selected: boolean, collapsed: boolean) => ({
  mx: "8px",
  mb: "2px",
  px: collapsed ? "12px" : "16px",
  py: "8px",
  minHeight: ITEM_HEIGHT,
  // MUI gives ListItemButton flexGrow:1 so it fills a ListItem. Inert inside
  // <List> (display:block), but items sitting directly in this flex column
  // would otherwise stretch to split the rail's leftover height.
  flexGrow: 0,
  flexShrink: 0,
  justifyContent: collapsed ? "center" : "flex-start",
  // radius.control is 4px here; `borderRadius: 2` would mean 2*8 = 16px.
  borderRadius: `${tokens.radius.control}px`,
  position: "relative" as const,
  color: selected ? tokens.color.railBg : onChrome.secondary,
  transition: `background ${tokens.motion.base} ${tokens.motion.easing}, color ${tokens.motion.base}`,
  // MUI's .Mui-selected is a two-class selector and outranks a plain sx
  // background, so the selected styles have to be nested under it or the
  // white never lands and navy text sits on the navy rail.
  "&.Mui-selected": {
    bgcolor: onChrome.strong,
    color: tokens.color.railBg,
    // Must restate colour, not just bgcolor: the generic &:hover below sets
    // white text, which on the white selected pill erases the label.
    "&:hover": { bgcolor: onChrome.strong, color: tokens.color.railBg }
  },
  "&:hover": { bgcolor: onChrome.hover, color: onChrome.strong },
  // Green marker is the only place green appears in the chrome.
  "&::before": selected
    ? {
        content: '""',
        position: "absolute",
        left: -8,
        top: 8,
        bottom: 8,
        width: 3,
        borderRadius: "0 3px 3px 0",
        bgcolor: tokens.color.positive.main
      }
    : undefined
});

const iconSx = (collapsed: boolean) => ({
  minWidth: 0,
  mr: collapsed ? 0 : "16px",
  color: "inherit",
  justifyContent: "center"
});

const resolveIcon = (name: string) => {
  const Cmp = NAV_ICONS[name] ?? NAV_ICON_FALLBACK;
  return <Cmp sx={{ fontSize: 24 }} />;
};

interface RailItemProps {
  label: string;
  icon: React.ReactNode;
  collapsed: boolean;
  selected?: boolean;
  onClick?: () => void;
}

/**
 * One row of the rail. Every entry — nav item, program jump, System Management
 * — is this; collapsing hides the label and moves it into the tooltip, which is
 * the only reason the collapsed rail stays usable.
 */
const RailItem = ({ label, icon, collapsed, selected = false, onClick }: RailItemProps) => {
  const button = (
    <ListItemButton selected={selected} onClick={onClick} sx={itemSx(selected, collapsed)}>
      <ListItemIcon sx={iconSx(collapsed)}>{icon}</ListItemIcon>
      {!collapsed && (
        <ListItemText
          primary={label}
          primaryTypographyProps={{
            fontSize: tokens.typography.body,
            fontWeight: selected ? 600 : 500,
            letterSpacing: ".00938em",
            noWrap: true
          }}
        />
      )}
    </ListItemButton>
  );

  return collapsed ? (
    <Tooltip title={label} placement="right">
      {button}
    </Tooltip>
  ) : (
    button
  );
};

const NavRail = (props: ShellChromeProps) => {
  const {
    nav,
    selectedItemId,
    onSelectItem,
    variant,
    onExitSystem,
    contextLabel,
    user,
    onSelectProgram,
    onOpenSystemManagement,
    onLogout,
    collapsed = false,
    onToggleCollapsed
  } = props;

  const bg = variant === "system" ? tokens.color.railSystemBg : tokens.color.railBg;

  return (
    // Outer wrapper does NOT clip: the collapse toggle straddles the rail's
    // right edge, and the scroll container is the inner <nav>. Putting overflow
    // here would cut the toggle in half.
    <Box
      sx={{
        position: "relative",
        flex: "none",
        // 100% so it fills the Drawer paper below md, where it is not a flex
        // child of the shell and would otherwise collapse to content height.
        height: "100%",
        width: collapsed ? RAIL_COLLAPSED_WIDTH : tokens.layout.railWidth,
        transition: `width ${tokens.motion.base} ${tokens.motion.easing}`
      }}
    >
      <Box
        component="nav"
        sx={{
          height: "100%",
          bgcolor: bg,
          display: "flex",
          flexDirection: "column",
          // The rail stays put while <main> scrolls; it only scrolls internally
          // if the nav itself outgrows the viewport.
          overflowY: "auto",
          overflowX: "hidden",
          // No top padding: it would sit above the 64px logo box and push the
          // logo below the optical centre of the region above the divider.
          pb: "8px"
        }}
      >
        {/* Toggle hangs outside the rail, so the logo centres on the full
            width rather than on whatever space the toggle leaves. */}
        <Box
          sx={{
            height: 64,
            flex: "none",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            px: "8px"
          }}
        >
          {!collapsed && (
            <Box
              component="img"
              src={require("../../../assets/logo-mark.png")}
              alt="Zintoro"
              sx={{ height: LOGO_HEIGHT, display: "block" }}
            />
          )}
        </Box>
        <Divider sx={{ borderColor: onChrome.divider, mb: "8px" }} />

        {variant === "system" && (
          <RailItem
            label={`Back to ${contextLabel ?? "dashboard"}`}
            icon={<ChevronLeft sx={{ fontSize: 24 }} />}
            collapsed={collapsed}
            onClick={onExitSystem}
          />
        )}

        {nav.groups.map((group) => (
          <React.Fragment key={group.id}>
            {collapsed ? (
              <Box sx={{ height: "16px" }} />
            ) : (
              <Typography sx={groupLabelSx}>{group.label}</Typography>
            )}
            <List disablePadding>
              {group.items.map((item) => (
                <RailItem
                  key={item.id}
                  label={item.label}
                  icon={resolveIcon(item.icon)}
                  collapsed={collapsed}
                  selected={item.id === selectedItemId}
                  onClick={() => onSelectItem(item.id)}
                />
              ))}
            </List>
          </React.Fragment>
        ))}

        {nav.otherPrograms.length > 0 && (
          <>
            <Divider sx={{ borderColor: onChrome.divider, mx: "16px", my: "12px" }} />
            {!collapsed && <Typography sx={groupLabelSx}>Other programs</Typography>}
            <List disablePadding>
              {nav.otherPrograms.map((p) => (
                <RailItem
                  key={p.programIndex}
                  label={p.label}
                  icon={<GridViewOutlined sx={{ fontSize: 24 }} />}
                  collapsed={collapsed}
                  onClick={() => onSelectProgram?.(p.programIndex)}
                />
              ))}
            </List>
          </>
        )}

        <Box sx={{ flex: 1 }} />

        {nav.showSystemManagement && variant === "client" && (
          <>
            <Divider sx={{ borderColor: onChrome.divider, my: "8px" }} />
            <RailItem
              label="System Management"
              icon={<TuneOutlined sx={{ fontSize: 24 }} />}
              collapsed={collapsed}
              onClick={onOpenSystemManagement}
            />
          </>
        )}

        {/* Identity is a label, not a control — logging out is its own button. */}
        <Box
          sx={{
            ...itemSx(false, collapsed),
            display: "flex",
            alignItems: "center",
            mb: 0,
            "&:hover": undefined
          }}
        >
          <Box sx={{ ...iconSx(collapsed), display: "flex" }}>
            <Avatar
              sx={{
                width: 32,
                height: 32,
                bgcolor: tokens.color.positive.main,
                color: tokens.color.ink,
                fontSize: tokens.typography.body,
                fontWeight: 500
              }}
            >
              {user?.initial ?? ""}
            </Avatar>
          </Box>
          {!collapsed && (
            <Box sx={{ minWidth: 0 }}>
              <Typography
                sx={{ fontSize: tokens.typography.body, lineHeight: 1.2, color: onChrome.strong }}
                noWrap
              >
                {user?.name ?? ""}
              </Typography>
              <Typography
                sx={{ fontSize: tokens.typography.caption, lineHeight: 1.2, color: onChrome.muted }}
                noWrap
              >
                {user?.roleLabel ?? ""}
              </Typography>
            </Box>
          )}
        </Box>

        <LogoutButton onLogout={onLogout} compact={collapsed} sx={{ mx: "8px", mt: "6px" }} />
      </Box>

      {onToggleCollapsed && (
        <Tooltip title={collapsed ? "Expand" : "Collapse"} placement="right">
          <IconButton
            onClick={onToggleCollapsed}
            size="small"
            aria-label={collapsed ? "Expand navigation" : "Collapse navigation"}
            sx={{
              position: "absolute",
              top: 26,
              right: -13,
              zIndex: 2,
              width: 26,
              height: 26,
              bgcolor: tokens.color.surface,
              color: tokens.color.body,
              border: `1px solid ${tokens.color.border}`,
              boxShadow: tokens.shadow.card,
              "&:hover": { bgcolor: tokens.color.surface, color: tokens.color.primary }
            }}
          >
            {collapsed ? (
              <ChevronRight sx={{ fontSize: 16 }} />
            ) : (
              <ChevronLeft sx={{ fontSize: 16 }} />
            )}
          </IconButton>
        </Tooltip>
      )}
    </Box>
  );
};

export default NavRail;
